import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { ReactNode } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Scrollbar } from 'src/components/atoms/scrollbar';
import { DataStatusIndicator } from 'src/components/molecules/data-status-indicator/data-status-indicator';
import { useEmployeesGroupsByTokens } from 'src/features/employees-groups/api/hooks/use-employees-groups-by-tokens';
import { useSpacesBySpaceGroupTokens } from 'src/features/spaces/api/hooks/use-spaces-by-space-group-tokens';
import { SetSortController } from 'src/hooks/use-sort-controller';
import { SortOrder } from 'src/types/enum/sort-order';
import { SortController } from 'src/types/interfaces/sort-controller';
import { TableColumn } from 'src/types/interfaces/table-column';
import { handleUpdateSort } from 'src/utils/sort/handle-update-sort';
import { SpaceGroupResponse } from '../../api/types/response/space-group.response';
import {
  SpaceGroupsActionsSlotPayload,
  SpaceGroupsTableRow,
} from '../molecules/space-groups-table-row';

interface Props {
  data: SpaceGroupResponse[];
  isLoading: boolean;
  error: string | undefined;
  sortController: SortController;
  setSortController: SetSortController;
  handleChangeSort: (sort: string[]) => void;
  actionsSlot: (payload: SpaceGroupsActionsSlotPayload) => ReactNode;
}

export const SpaceGroupsTable = ({
  data,
  isLoading,
  error,
  sortController,
  setSortController,
  handleChangeSort,
  actionsSlot,
}: Props) => {
  const { t } = useTranslation(['spaceGroups']);

  const spaceGroupsTokens = data?.map(({ token }) => token);

  const { results: spaces } = useSpacesBySpaceGroupTokens(spaceGroupsTokens);

  const hasAnySpacesAdded = (token: string) =>
    spaces?.hits?.some(({ spaceGroupToken }) => spaceGroupToken === token) ??
    false;

  const employeeGroupsTokens = data
    ?.map(({ employeeGroupsTokens }) => employeeGroupsTokens)
    .flat();

  const { results: employeesGroups } =
    useEmployeesGroupsByTokens(employeeGroupsTokens);

  const employeesGroupsNames = (employeesGroupsTokens: string[]) =>
    employeesGroups?.hits
      ?.filter(({ token }) => employeesGroupsTokens.includes(token))
      .map(({ name }) => name) ?? [];

  const columns: TableColumn[] = [
    {
      id: 'name',
      label: t('common.columns.name'),
      isSortable: true,
    },
    {
      id: 'employeeGroups',
      label: t('common.columns.employeeGroups'),
      isSortable: false,
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}>
      <Scrollbar autoHide={false}>
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              {columns.map(column =>
                column.isSortable ? (
                  <TableCell key={column.id}>
                    <TableSortLabel
                      active={true}
                      direction={
                        sortController.sortBy === column.id
                          ? sortController.sortOrder
                          : SortOrder.ASC
                      }
                      disabled={isLoading}
                      onClick={() =>
                        handleUpdateSort(
                          setSortController,
                          handleChangeSort,
                          column.id,
                        )
                      }
                      key={column.id}>
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ) : (
                  <TableCell key={column.id}>{column.label}</TableCell>
                ),
              )}

              <TableCell />
            </TableRow>
          </TableHead>

          <TableBody>
            {data?.map(({ token, name, employeeGroupsTokens }) => {
              return (
                <SpaceGroupsTableRow
                  key={token}
                  token={token}
                  name={name}
                  employeesGroupsTokens={employeeGroupsTokens}
                  employeesGroupsNames={employeesGroupsNames(
                    employeeGroupsTokens,
                  )}
                  actionsSlot={actionsSlot}
                  hasAnySpacesAdded={hasAnySpacesAdded(token)}
                />
              );
            })}
          </TableBody>
        </Table>
      </Scrollbar>

      <DataStatusIndicator
        dataLength={data?.length}
        error={error}
        isFetching={isLoading}
      />
    </Box>
  );
};
